import '../styles/fleet.css';

import { store } from '../state';
import { i18n } from '../i18n';
import { vesselsAPI } from '../api';
import type { AppState, UUID } from '../types';
import { renderTemplate } from '../template-engine';
import { buildTemplateData, formatters } from '../template-utils';

import vesselCardTpl from '../templates/vessel-card.html?raw';

const els = {
  fleetListContainer: document.getElementById('fleet-list-container') as HTMLElement,
  vesselDetailsContainer: document.getElementById('vessel-details-container') as HTMLElement,  
};

export function initEventListeners() {
  // Делегирование событий для списка флота
  els.fleetListContainer.addEventListener('click', (e) => {
    const item = ((e.target as HTMLElement).closest('.vessel-list-item')) as HTMLElement | null;
    if (item?.dataset?.id) {
      const id = item.dataset.id;
      const vessel = store.getState().vesselsList.find(v => v.id === id);
      if (vessel) {
        store.dispatch({ type: 'SELECT_VESSEL', payload: vessel });
        fetchAndSetVesselPhoto(id);
      }
    }
  });
}

async function fetchAndSetVesselPhoto(id: UUID) {
  const state = store.getState();
  
  // Явная проверка на null перед обращением к свойствам
  if (state.selectedVessel && state.selectedVessel.id === id && !state.selectedVessel.photo) {
    try {
      const photoUrl = await vesselsAPI.getPhoto(id);
      store.dispatch({ type: 'SET_VESSEL_PHOTO', payload: { id, photo: photoUrl } });
    } catch (err) {
      console.log('Фото судна не найдено или ошибка загрузки');
    }
  }
}

export async function loadVessels() {
  const state = store.getState();
  if (state.vesselsList.length > 0 || state.vesselsLoading) return;

  store.dispatch({ type: 'SET_VESSELS_LOADING', payload: true });
  try {
    const response = await vesselsAPI.list({ limit: 100 });
    store.dispatch({ type: 'SET_VESSELS_LIST', payload: response.data });
    
    // АВТОВЫБОР: если ничего не выбрано, выбираем первый элемент
    if (!state.selectedVessel && response.data.length > 0) {
      const firstVessel = response.data[0];
      store.dispatch({ type: 'SELECT_VESSEL', payload: firstVessel });
      // ТРИГГЕР ЗАГРУЗКИ ФОТО для активированного элемента
      fetchAndSetVesselPhoto(firstVessel.id);
    }
  } catch (error) {
    console.error('Ошибка загрузки флота:', error);
    store.dispatch({ type: 'SET_VESSELS_ERROR', payload: i18n.t('fleet.error.load') });
  }
}

const formatDate = (isoString?: string | null) => {
  if (!isoString) return i18n.t('ui.not_specified');
  try { return new Date(isoString).toLocaleString(i18n.lang === 'ru' ? 'ru-RU' : 'en-US'); } 
  catch { return i18n.t('ui.unknown'); }
};

// НОВАЯ ФУНКЦИЯ: Рендер раздела флота

export function renderFleet(state: AppState) {
  // 1. Рендер списка (левая колонка)
  if (state.vesselsLoading) {
    els.fleetListContainer.innerHTML = `
      <div class="state-view loading" style="min-height: 150px;">
        <div class="spinner" style="width: 30px; height: 30px;"></div>
      </div>`;
  } else if (state.vesselsError) {
    els.fleetListContainer.innerHTML = `<div class="state-view error"><p>${state.vesselsError}</p></div>`;
  } else if (state.vesselsList.length === 0) {
    els.fleetListContainer.innerHTML = `<div class="state-view empty"><p data-i18n="fleet.empty_list">Список пуст</p></div>`;
  } else {
    els.fleetListContainer.innerHTML = state.vesselsList.map(v => {
      const isActive = state.selectedVessel?.id === v.id;
      const dateRange = `${formatDate(v.date_from)} – ${formatDate(v.date_to)}`;
      const ownedBadge = v.owned ? `<span class="badge badge-owned">${i18n.t('fleet.owned')}</span>` : '';
      
      return `
        <div class="vessel-list-item ${isActive ? 'active' : ''}" data-id="${v.id}">
          <div class="item-title">${escapeHtml(v.name)}</div>
          <div class="item-meta">
            <span class="item-date">${dateRange}</span>
            ${ownedBadge}
          </div>
        </div>
      `;
    }).join('');
  }

  // 2. Рендер деталей (правая колонка)
  if (!state.selectedVessel) {
    els.vesselDetailsContainer.innerHTML = `
      <div class="stub-content">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="color: var(--accent-color); margin-bottom: 16px;">
          <path d="M2 20h20"></path><path d="M5 20V8l7-5 7 5v12"></path><path d="M9 20v-6h6v6"></path>
        </svg>
        <h2 data-i18n="fleet.select_prompt">Выберите судно</h2>
        <p data-i18n="fleet.select_prompt_desc">для просмотра подробной информации</p>
      </div>
    `;
  } else {
    const v = state.selectedVessel;

    const templateData = buildTemplateData(
      v,
      {
        name: { key: 'name' }, // По умолчанию применяется formatters.escape
        type: { key: 'type', fallback: i18n.t('ui.not_specified') },
        dateFrom: { key: 'date_from', format: formatters.date },
        dateTo: { key: 'date_to', format: formatters.date, fallback: i18n.t('fleet.present') },
        length: { key: 'length', format: (val) => formatters.withUnit(val, 'м') },
        crew: { key: 'crew', format: (val) => formatters.withUnit(val, 'чел.') },
        owned: { key: 'owned', format: (val) => !!val }, // Булево значение для {{#if owned}}
        ownedText: { key: 'owned', format: (val) => val ? i18n.t('fleet.yes') : i18n.t('fleet.no') },
        descriptionHtml: { key: 'description', format: formatters.html, fallback: null },
      },
      {
        // Дополнительные поля, не требующие сложного маппинга из source
        photo: v.photo || null,
        i18n: {
          fleet: {
            type: i18n.t('fleet.type'),
            date_from: i18n.t('fleet.date_from'),
            date_to: i18n.t('fleet.date_to'),
            length: i18n.t('fleet.length'),
            crew: i18n.t('fleet.crew'),
            owned_flag: i18n.t('fleet.owned_flag'),
            description: i18n.t('fleet.description')
          }
        }
      }
    );

    els.vesselDetailsContainer.innerHTML = renderTemplate(vesselCardTpl, templateData);
  }
}

const escapeHtml = (str: string | undefined | null): string => {
  if (str == null) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
};

